import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { validators } from '@/services/api';

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { user, updateCredentials } = useAuth();
  const [loginId, setLoginId] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoginId(user?.email || '');
  }, [user?.email]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user || saving) return;

    const normalizedLoginId = loginId.trim().toLowerCase();
    const loginIdChanged = normalizedLoginId !== user.email.toLowerCase();
    const passwordChanged = Boolean(newPassword);

    setError('');
    setSuccess('');

    if (!loginIdChanged && !passwordChanged) {
      setError('변경할 로그인 아이디 또는 새 비밀번호를 입력해 주세요.');
      return;
    }
    if (loginIdChanged) {
      const loginIdError = validators.email(normalizedLoginId);
      if (loginIdError) {
        setError(loginIdError);
        return;
      }
    }
    if (!currentPassword) {
      setError('현재 비밀번호를 입력해 주세요.');
      return;
    }
    if (passwordChanged) {
      const passwordError = validators.password(newPassword);
      if (passwordError) {
        setError(passwordError);
        return;
      }
      const confirmError = validators.passwordConfirm(newPassword, passwordConfirm);
      if (confirmError) {
        setError(confirmError);
        return;
      }
    }

    setSaving(true);
    const result = await updateCredentials({
      ...(loginIdChanged ? { email: normalizedLoginId } : {}),
      currentPassword,
      ...(passwordChanged ? { newPassword } : {}),
    });
    setSaving(false);

    if (!result.success) {
      setError(result.error || '로그인 정보를 변경하지 못했습니다.');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setPasswordConfirm('');
    setSuccess(result.message || '로그인 정보가 변경되었습니다.');
  };

  return (
    <div className="min-h-screen bg-background-50 pb-10">
      <header className="bg-white px-4 pt-11 pb-5 border-b border-background-200/70">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <button
            type="button"
            aria-label="프로필로 돌아가기"
            onClick={() => navigate('/profile')}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-50 text-foreground-700"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div>
            <p className="text-xs text-accent-500 font-semibold">개인 설정</p>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mt-0.5">로그인 정보 변경</h1>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 pt-6">
        <div className="rounded-2xl bg-accent-50 border border-accent-100 px-4 py-3.5 flex gap-3 mb-5">
          <i className="ri-shield-keyhole-line text-lg text-accent-600 mt-0.5"></i>
          <p className="text-xs leading-5 text-accent-800">
            안전을 위해 현재 비밀번호 확인이 필요합니다. 변경 후에는 다른 기기에서 다시 로그인해야 합니다.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <section className="bg-white rounded-2xl p-5 shadow-sm border border-background-200/70">
            <h2 className="font-bold text-foreground-900">로그인 아이디</h2>
            <p className="text-xs text-foreground-500 mt-1.5">로그인 아이디는 이메일 형식입니다.</p>
            <label className="block text-xs font-medium text-foreground-700 mt-4 mb-1.5" htmlFor="login-id">
              로그인 아이디 (이메일)
            </label>
            <input
              id="login-id"
              type="email"
              value={loginId}
              onChange={(event) => setLoginId(event.target.value)}
              autoComplete="username"
              className="w-full px-3.5 py-3 rounded-xl border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100"
            />
          </section>

          <section className="bg-white rounded-2xl p-5 shadow-sm border border-background-200/70">
            <h2 className="font-bold text-foreground-900">비밀번호 변경</h2>
            <p className="text-xs text-foreground-500 mt-1.5">바꾸지 않을 경우 새 비밀번호 칸은 비워 두세요.</p>
            <label className="block text-xs font-medium text-foreground-700 mt-4 mb-1.5" htmlFor="new-password">
              새 비밀번호
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              placeholder="영문·숫자 포함 8자 이상"
              className="w-full px-3.5 py-3 rounded-xl border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100"
            />
            <label className="block text-xs font-medium text-foreground-700 mt-4 mb-1.5" htmlFor="new-password-confirm">
              새 비밀번호 확인
            </label>
            <input
              id="new-password-confirm"
              type="password"
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              autoComplete="new-password"
              placeholder="새 비밀번호를 한 번 더 입력하세요"
              className="w-full px-3.5 py-3 rounded-xl border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100"
            />
          </section>

          <section className="bg-white rounded-2xl p-5 shadow-sm border border-background-200/70">
            <label className="block text-sm font-bold text-foreground-900" htmlFor="current-password">
              현재 비밀번호 확인
            </label>
            <p className="text-xs text-foreground-500 mt-1.5">아이디만 변경할 때도 현재 비밀번호를 입력해 주세요.</p>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="현재 비밀번호"
              className="w-full mt-4 px-3.5 py-3 rounded-xl border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100"
            />
          </section>

          {error && <p role="alert" className="px-4 py-3 rounded-xl bg-rose-50 text-sm text-rose-600">{error}</p>}
          {success && <p role="status" className="px-4 py-3 rounded-xl bg-emerald-50 text-sm text-emerald-700">{success}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3.5 rounded-xl bg-accent-500 text-white font-bold text-sm disabled:opacity-60 disabled:cursor-not-allowed hover:bg-accent-600 transition-colors"
          >
            {saving ? '변경 내용을 저장하는 중...' : '로그인 정보 저장'}
          </button>
        </form>
      </main>
    </div>
  );
}
